import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cullsApi } from '../services/api';
import { Cull } from '../types';
import './CullCard.css';

const CullCard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [cull, setCull] = useState<Cull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCull = async () => {
      if (!user?.uid || !id) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        const cullData = await cullsApi.getCull(id);
        if (cullData.created_by !== user.uid) {
          setError('Unauthorized access');
          return;
        }
        setCull(cullData);
      } catch (error: any) {
        console.error('Error fetching cull:', error);
        setError(`Error loading cull: ${error.message || error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCull();
  }, [user, id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  if (loading) {
    return (
      <div className="cull-card-container">
        <div className="loading">Loading cull details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cull-card-container">
        <div className="error">{error}</div>
        <Link to="/data" className="back-link">← Back to Data</Link>
      </div>
    );
  }

  if (!cull) {
    return (
      <div className="cull-card-container">
        <div className="error">Cull record not found</div>
        <Link to="/data" className="back-link">← Back to Data</Link>
      </div>
    );
  }

  return (
    <div className="cull-card-container">
      <div className="cull-card">
        <div className="card-header">
          <h1>Cull Record </h1>
          <div className="card-actions">
            <Link to={`/cull/${cull.id}`} className="edit-btn">
              ✏️ Edit
            </Link>
            <Link to="/data" className="back-btn">
              ← Back to Data
            </Link>
          </div>
        </div>

        <div className="card-content">
          <div className="info-section">
            <h2>📅 Date & Time</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Date:</label>
                <span>{formatDate(cull.date)}</span>
              </div>
              <div className="info-item">
                <label>Time:</label>
                <span>{formatTime(cull.time)}</span>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h2>📍 Location</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Location:</label>
                <span>{cull.location_name}</span>
              </div>
              {cull.latitude && cull.longitude && (
                <div className="info-item">
                  <label>Coordinates:</label>
                  <span>{cull.latitude}, {cull.longitude}</span>
                </div>
              )}
              {cull.grid_ref && (
                <div className="info-item">
                  <label>Grid Reference:</label>
                  <span>{cull.grid_ref}</span>
                </div>
              )}
            </div>
          </div>

          <div className="info-section">
            <h2>🦌 Animal Details</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Species:</label>
                <span className="species-badge">{cull.species}</span>
              </div>
              <div className="info-item">
                <label>Sex:</label>
                <span>{cull.sex}</span>
              </div>
              <div className="info-item">
                <label>Age Estimate:</label>
                <span>{cull.age_estimate}</span>
              </div>
              {cull.weight && (
                <div className="info-item">
                  <label>Weight:</label>
                  <span>{cull.weight} kg</span>
                </div>
              )}
              <div className="info-item">
                <label>Body Condition:</label>
                <span>{cull.condition}</span>
              </div>
              {cull.condition_notes && (
                <div className="info-item">
                  <label>Condition Notes:</label>
                  <span>{cull.condition_notes}</span>
                </div>
              )}
              <div className="info-item">
                <label>Antlers Present:</label>
                <span>{cull.antlers_present ? 'Yes' : 'No'}</span>
              </div>
              {cull.antlers_present && cull.antler_points && (
                <div className="info-item">
                  <label>Antler Points:</label>
                  <span>{cull.antler_points}</span>
                </div>
              )}
              {cull.antlers_present && cull.antler_condition && (
                <div className="info-item">
                  <label>Antler Condition:</label>
                  <span>{cull.antler_condition}</span>
                </div>
              )}
            </div>
          </div>

          <div className="info-section">
            <h2>🎯 Shot Details</h2>
            <div className="info-grid">
              {cull.shot_distance && (
                <div className="info-item">
                  <label>Distance:</label>
                  <span>{cull.shot_distance} meters</span>
                </div>
              )}
              <div className="info-item">
                <label>Placement:</label>
                <span>{cull.shot_placement}</span>
              </div>
            </div>
          </div>

          {cull.notes && (
            <div className="info-section">
              <h2>📝 Notes</h2>
              <div className="notes-content">
                {cull.notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CullCard;
